import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {MeshCellSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';

export enum ExtendSerieDirection
{
    Positive = 'positiveDirection',
    Negative = 'negativeDirection'
}

export enum ExtendSerieType
{
    Formula = 'formulaSerie',
    Number = 'numbersSerie',
    Date = 'dateSerie',
    Currency = 'currencySerie'
}

export interface IExtendSerie
{
    readonly type:ExtendSerieType;
    readonly size:number;
    addKnownValue(value:any):void;
    calculateNextValue(extendDirection:ExtendSerieDirection, isSingleSerie?:boolean):SlotFormula;

    readonly cellSettings:Array<MeshCellSettings>;
    addCellSettings(cellSettigs:MeshCellSettings):void;
    readonly cellFormats:Array<string>;
    addCellFormat(formatId:string):void;
    getCellSettings(extendDirection:ExtendSerieDirection):MeshCellSettings;
    getCellFormat(extendDirection:ExtendSerieDirection):string;
}

export class FormulasExtendSerie implements IExtendSerie
{
    public readonly cellFormats:Array<string> = [];
    private _cellFormatsIndex:number = null;
    public readonly type:ExtendSerieType = ExtendSerieType.Formula;
    private readonly _formulas:Array<SlotFormula> = [];
    private _lastIndex:number = null;

    public readonly cellSettings:Array<MeshCellSettings> = [];
    private _cellSettingsIndex:number = null;

    public get size():number
    {
        return this._formulas.length;
    }

    public addKnownValue(value:SlotFormula):void
    {
        this._formulas.push(value);
    }

    public addCellSettings(cellSettigs:MeshCellSettings):void
    {
        this.cellSettings.push(cellSettigs);
    }

    addCellFormat(formatId:string):void
    {
        this.cellFormats.push(formatId);
    }

    public calculateNextValue(extendDirection:ExtendSerieDirection):SlotFormula
    {
        if( this._lastIndex === null )
            this._lastIndex = extendDirection === ExtendSerieDirection.Positive
                ? this._formulas.length - 1
                : 0;

        if( extendDirection === ExtendSerieDirection.Positive )
        {
            if( this._lastIndex === null || this._lastIndex === (this._formulas.length - 1) )
                this._lastIndex = 0;
            else
                this._lastIndex++;
        }
        else
        {
            if( this._lastIndex === null || this._lastIndex === 0 )
                this._lastIndex = this._formulas.length - 1;
            else
                this._lastIndex--;
        }

        return this._formulas[this._lastIndex];
    }

    public getCellSettings(extendDirection:ExtendSerieDirection):MeshCellSettings
    {
        if( this._cellSettingsIndex === null )
            this._cellSettingsIndex = extendDirection === ExtendSerieDirection.Positive
                ? this.cellSettings.length - 1
                : 0;

        if( extendDirection === ExtendSerieDirection.Positive )
        {
            if( this._cellSettingsIndex === null || this._cellSettingsIndex === (this.cellSettings.length - 1) )
                this._cellSettingsIndex = 0;
            else
                this._cellSettingsIndex++;
        }
        else
        {
            if( this._cellSettingsIndex === null || this._cellSettingsIndex === 0 )
                this._cellSettingsIndex = this.cellSettings.length - 1;
            else
                this._cellSettingsIndex--;
        }

        return this.cellSettings[this._cellSettingsIndex];
    }

    public getCellFormat(extendDirection:ExtendSerieDirection):string
    {
        if( this._cellFormatsIndex === null )
            this._cellFormatsIndex = extendDirection === ExtendSerieDirection.Positive
                ? this.cellFormats.length - 1
                : 0;

        if( extendDirection === ExtendSerieDirection.Positive )
        {
            if( this._cellFormatsIndex === null || this._cellFormatsIndex === (this.cellFormats.length - 1) )
                this._cellFormatsIndex = 0;
            else
                this._cellFormatsIndex++;
        }
        else
        {
            if( this._cellFormatsIndex === null || this._cellFormatsIndex === 0 )
                this._cellFormatsIndex = this.cellFormats.length - 1;
            else
                this._cellFormatsIndex--;
        }

        return this.cellFormats[this._cellFormatsIndex];
    }
}

export class NumbersExtendSerie implements IExtendSerie
{
    public static START_INDEX:number = 1;

    public readonly type:ExtendSerieType = ExtendSerieType.Number;
    private readonly _numbers:Array<number> = [];
    private _lastIndex:number = null;
    private _numbersSum:number = null;
    private _indexesSum:number = null;
    private _indexesSquareSum:number = null;
    private _indexMulNumberSum:number = null;
    private _slope:number = null;
    private _intercept:number = null;

    public readonly cellSettings:Array<MeshCellSettings> = [];
    public readonly cellFormats:Array<string> = [];
    private _cellFormatsIndex:number = null;
    private _cellSettingsIndex:number = null;

    private _init():void
    {
        this._numbersSum = this._numbers.reduce(
            (previousValue:number, currentNumber:number) => previousValue + currentNumber,
            0
        );
        this._indexesSum = (NumbersExtendSerie.START_INDEX + this._numbers.length) * this._numbers.length / 2;

        let currentIndex:number = NumbersExtendSerie.START_INDEX;

        this._indexesSquareSum = this._numbers.reduce(
            (previousValue:number) =>
            {
                const nextValue:number = previousValue + (currentIndex * currentIndex);

                currentIndex++;

                return nextValue;
            },
            0
        );

        currentIndex = NumbersExtendSerie.START_INDEX;

        this._indexMulNumberSum = this._numbers.reduce(
            (previousValue:number, currentNumber:number) =>
            {
                const nextValue:number = previousValue + currentNumber * currentIndex;

                currentIndex++;

                return nextValue;
            },
            0
        );

        this._slope = (this._numbers.length * this._indexMulNumberSum - this._indexesSum * this._numbersSum) / (this._numbers.length * this._indexesSquareSum - this._indexesSum * this._indexesSum);
        this._intercept = (this._numbersSum - this._slope * this._indexesSum) / this._numbers.length;
    }

    public get size():number
    {
        return this._numbers.length;
    }

    public addKnownValue(value:number):void
    {
        this._numbers.push(value);
    }

    public addCellSettings(cellSettigs:MeshCellSettings):void
    {
        this.cellSettings.push(cellSettigs);
    }

    addCellFormat(formatId:string):void
    {
        this.cellFormats.push(formatId);
    }

    public calculateNextValue(extendDirection:ExtendSerieDirection, isSingleSerie:boolean):SlotFormula
    {
        if( this.size === 1 && isSingleSerie )
            return String(this._numbers[0]);
        else if( this.size === 1 )
        {
            if( this._lastIndex === null )
                this._lastIndex = this._numbers[0];

            if( extendDirection === ExtendSerieDirection.Positive )
                this._lastIndex++;
            else
                this._lastIndex--;

            return String(this._lastIndex);
        }
        else
        {
            if( this._numbersSum === null )
                this._init();

            if( this._lastIndex === null )
                this._lastIndex = extendDirection === ExtendSerieDirection.Positive
                    ? NumbersExtendSerie.START_INDEX + this._numbers.length - 1
                    : NumbersExtendSerie.START_INDEX;

            if( extendDirection === ExtendSerieDirection.Positive )
                this._lastIndex++;
            else
                this._lastIndex--;

            return String(this._slope * this._lastIndex + this._intercept);
        }
    }

    public getCellSettings(extendDirection:ExtendSerieDirection):MeshCellSettings
    {
        if( this._cellSettingsIndex === null )
            this._cellSettingsIndex = extendDirection === ExtendSerieDirection.Positive
                ? this.cellSettings.length - 1
                : 0;

        if( extendDirection === ExtendSerieDirection.Positive )
        {
            if( this._cellSettingsIndex === null || this._cellSettingsIndex === (this.cellSettings.length - 1) )
                this._cellSettingsIndex = 0;
            else
                this._cellSettingsIndex++;
        }
        else
        {
            if( this._cellSettingsIndex === null || this._cellSettingsIndex === 0 )
                this._cellSettingsIndex = this.cellSettings.length - 1;
            else
                this._cellSettingsIndex--;
        }

        return this.cellSettings[this._cellSettingsIndex];
    }

    public getCellFormat(extendDirection:ExtendSerieDirection):string
    {
        if( this._cellFormatsIndex === null )
            this._cellFormatsIndex = extendDirection === ExtendSerieDirection.Positive
                ? this.cellFormats.length - 1
                : 0;

        if( extendDirection === ExtendSerieDirection.Positive )
        {
            if( this._cellFormatsIndex === null || this._cellFormatsIndex === (this.cellFormats.length - 1) )
                this._cellFormatsIndex = 0;
            else
                this._cellFormatsIndex++;
        }
        else
        {
            if( this._cellFormatsIndex === null || this._cellFormatsIndex === 0 )
                this._cellFormatsIndex = this.cellFormats.length - 1;
            else
                this._cellFormatsIndex--;
        }

        return this.cellFormats[this._cellFormatsIndex];
    }
}

export interface IExtendData
{
    readonly series:Array<Array<IExtendSerie>>;
}
